import { useEffect, useRef, useState } from 'react';
import { spore } from '@/spore/runtime';
import { Panel } from './Panel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SignalHealthPanel = () => {
  const t = window.__spore.touch;
  const b = t.baseline;
  const up = window.__spore.uploads;
  const f = t.features || {};
  return (
    <Panel testId="signal-health-panel" code="00c / HEALTH" title="Signal Health · Rolling Baseline" right={<button data-testid="save-snapshot-button" className="btn-lab px-3 py-1 text-[0.65rem]" onClick={() => spore.saveSnapshot()}>Save 60 s snapshot (X)</button>}>
      <div className="grid grid-cols-2 gap-3 text-sm lg:grid-cols-4">
        <div><div className="label-eyebrow">Baseline age</div><div data-testid="health-baseline-age" className="font-display text-2xl font-bold">{b.ageS.toFixed(0)} s</div></div>
        <div><div className="label-eyebrow">Touch active for</div><div data-testid="health-touch-duration" className={`font-display text-2xl font-bold ${t.touchMs > 15000 ? 'glow-amber' : ''}`}>{(t.touchMs / 1000).toFixed(1)} s</div></div>
        <div><div className="label-eyebrow">Auto re-baselines</div><div data-testid="health-rebaselines" className="font-display text-2xl font-bold">{b.rebaselines}</div></div>
        <div><div className="label-eyebrow">Uploads</div><div data-testid="health-uploads" className="font-display text-2xl font-bold">{up.count}</div></div>
      </div>
      <div data-testid="health-medians" className="mt-3 text-xs text-[var(--muted)]">
        baseline motion median {b.median.raw.toFixed(1)} · spread {b.spread.toFixed(1)} · CC90 level {b.median.level.toFixed(0)}
      </div>
      <div data-testid="health-features" className="mt-1 text-xs text-[var(--muted)]">motion now: CC {f.ccMotion.toFixed(0)} + pitch {f.pitchMotion.toFixed(0)} = {f.raw.toFixed(0)} → activity {t.activity.toFixed(2)} ({t.band})</div>
      {up.last && <div data-testid="health-last-upload" className="mt-1 text-xs text-[var(--spore)]">last upload: {up.last.kind} · {up.last.recording_id} · {up.last.n_events} events</div>}
      {up.error && <div className="mt-1 text-xs text-[var(--coral)]">upload error: {up.error}</div>}
    </Panel>
  );
};

const BAND_COLOR = { NONE: 'var(--dim)', LOW: '#7f9cf5', MID: '#ff9ec7', HIGH: '#ffd166' };

export const ActivityPanel = () => {
  const t = window.__spore.touch;
  const th = t.thresholds.bands;
  return (
    <Panel testId="activity-panel" code="00b / ACTIVITY" title="Touch Level · Activity Envelope" right={<button data-testid="relearn-baseline-button" className="btn-lab px-3 py-1 text-[0.65rem]" onClick={() => spore.relearnBaseline(10000)}>{t.learning ? `Learning… ${(t.learnRemainingMs / 1000).toFixed(0)}s` : 'Re-learn base (B)'}</button>}>
      <div className="flex items-center gap-6">
        <div data-testid="activity-band" className="font-display w-28 text-4xl font-extrabold" style={{ color: BAND_COLOR[t.band] }}>{t.band}</div>
        <div className="relative h-8 flex-1 border border-white/15">
          <div data-testid="activity-bar" className="h-full transition-[width] duration-100" style={{ width: `${Math.round(t.activity * 100)}%`, background: BAND_COLOR[t.band], boxShadow: `0 0 20px ${BAND_COLOR[t.band]}` }} />
          {Object.entries(th).map(([k, v]) => <div key={k} className="absolute top-0 h-full border-l border-dashed border-white/40" style={{ left: `${v * 100}%` }}><span className="ml-1 text-[0.6rem] text-[var(--muted)]">{k}</span></div>)}
        </div>
        <div data-testid="activity-value" className="font-display w-20 text-right text-2xl font-bold">{t.activity.toFixed(2)}</div>
      </div>
      <div className="mt-3 flex items-center gap-3 text-xs text-[var(--muted)]">
        <span>Touch sensitivity</span>
        <input data-testid="sensitivity-slider" type="range" min="1" max="5" value={t.sensitivity} onChange={(e) => spore.setSensitivity(Number(e.target.value))} />
        <b data-testid="sensitivity-value" className="text-[var(--ink)]">{t.sensitivity}/5</b>
        <span className="ml-4">bands use hysteresis ±0.05 and a 0.4 s minimum dwell; smoothed 0.25 s attack / 0.45 s release</span>
      </div>
    </Panel>
  );
};

export const RecordingsPanel = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => fetch(`${API}/recordings`).then((r) => r.json()).then((d) => setList(d.recordings || [])).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);
  const show = async (rid) => setOpen(await (await fetch(`${API}/recordings/${rid}`)).json());
  return (
    <Panel testId="recordings-panel" code="11 / RECORDINGS" title="Raw Data on Server" right={<button data-testid="recordings-refresh" className="btn-lab px-3 py-1 text-[0.65rem]" onClick={load}>Refresh</button>}>
      <div className="log-scroll max-h-[220px] overflow-y-auto text-xs">
        {list.length ? list.map((r) => (
          <button key={r.recording_id} data-testid={`recording-${r.recording_id}`} className="flex w-full justify-between border-b border-white/5 py-1 text-left hover:text-[var(--spore)]" onClick={() => show(r.recording_id)}>
            <span>{new Date(r.created_at).toLocaleString()} · <b>{r.kind}</b> {r.label}</span><span>{r.n_events} ev · {r.n_detections} det</span>
          </button>
        )) : <div className="text-[var(--dim)]">no recordings yet</div>}
      </div>
      {open && <div data-testid="recording-detail" className="mt-3 text-xs text-[var(--muted)]">{open.recording_id}: {open.kind} · {open.events.length} events · {open.detections.length} detections · thresholds {JSON.stringify(open.thresholds)}</div>}
    </Panel>
  );
};

export const VoiceAuditionPanel = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const [sub, setSub] = useState(null);
  useEffect(() => {
    fetch(`${API}/voice/candidates`).then((r) => r.json()).then(setD);
    fetch(`${API}/eleven/subscription`).then((r) => r.json()).then(setSub).catch(() => {});
  }, []);
  const play = async (vid) => {
    const { unlockAudio, playEerie } = await import('@/spore/eerieAudio');
    const c = await unlockAudio();
    setBusy(vid);
    try {
      const r = await fetch(`${API}/voice/audition/${vid}`);
      playEerie(await c.decodeAudioData(await r.arrayBuffer()), { fx: false });
    } finally {
      setBusy(null);
    }
  };
  const choose = async (vid) => {
    const r = await (await fetch(`${API}/voice/choose`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ voice_id: vid }) })).json();
    setD((x) => ({ ...x, chosen: r.chosen }));
  };
  if (!d) return null;
  return (
    <Panel testId="voice-audition-panel" code="12 / AUDITION" title={`Professeur Voice Audition · ElevenLabs ${d.model}`}>
      {sub && <div data-testid="eleven-subscription" className="mb-3 text-xs text-[var(--muted)]">tier <b className="text-[var(--ink)]">{sub.tier}</b> · used {sub.character_count?.toLocaleString()} / {sub.character_limit?.toLocaleString()} credits · remaining {sub.remaining?.toLocaleString()}</div>}
      <div className="mb-3 text-xs italic text-[var(--muted)]">“{d.audition_line}”</div>
      {d.candidates.map((c) => (
        <div key={c.voice_id} data-testid={`voice-candidate-${c.voice_id}`} className="flex items-center justify-between gap-3 border-b border-white/5 py-2 text-sm">
          <div><b>{c.name}</b> {d.chosen === c.voice_id && <span data-testid="voice-chosen-badge" className="ml-2 text-xs text-[var(--spore)]">● IN USE</span>}<div className="text-xs text-[var(--muted)]">{c.why} · {c.voice_id}</div></div>
          <div className="flex gap-2">
            <button data-testid={`voice-play-${c.voice_id}`} className="btn-lab px-2 py-1 text-[0.65rem]" disabled={busy === c.voice_id} onClick={() => play(c.voice_id)}>{busy === c.voice_id ? '…' : 'Play'}</button>
            <button data-testid={`voice-use-${c.voice_id}`} className="btn-lab primary px-2 py-1 text-[0.65rem]" disabled={d.chosen === c.voice_id} onClick={() => choose(c.voice_id)}>Use this voice</button>
          </div>
        </div>
      ))}
      <div className="mt-2 text-xs text-[var(--dim)]">After switching voice press G on stage (or regenerate) so the cached script is re-voiced.</div>
    </Panel>
  );
};

export const SfxBankPanel = () => {
  const [bank, setBank] = useState(null);
  const [job, setJob] = useState(null);
  const ctx = useRef(null);
  const load = () => fetch(`${API}/sfx/bank/current`).then((r) => (r.ok ? r.json() : null)).then(setBank);
  useEffect(() => {
    load();
  }, []);
  const regen = async () => {
    const j = await (await fetch(`${API}/sfx/bank/generate`, { method: 'POST' })).json();
    setJob(j);
    for (;;) {
      await new Promise((r) => setTimeout(r, 2500));
      const st = await (await fetch(`${API}/sfx/bank/status/${j.job_id}`)).json();
      setJob(st);
      if (st.status !== 'generating') break;
    }
    load();
  };
  const play = async (c) => {
    const { unlockAudio, getMaster } = await import('@/spore/eerieAudio');
    ctx.current = await unlockAudio();
    const buf = await ctx.current.decodeAudioData(await (await fetch(`${API}/professor/audio/${c.audio_path}`)).arrayBuffer());
    const src = ctx.current.createBufferSource();
    src.buffer = buf;
    src.connect(getMaster());
    src.start();
    if (c.loop) setTimeout(() => src.stop(), 4000);
  };
  return (
    <Panel testId="sfx-bank-panel" code="13 / SFX" title="Mushroom Noise Bank · ElevenLabs SFX" right={<button data-testid="sfx-regenerate-button" className="btn-lab px-3 py-1 text-[0.65rem]" disabled={job && job.status === 'generating'} onClick={regen}>{job && job.status === 'generating' ? `Generating ${job.done}/${job.total}` : 'Regenerate bank'}</button>}>
      {bank ? (
        <>
          <div data-testid="sfx-bank-summary" className="mb-2 text-xs text-[var(--muted)]">{bank.bank_id} · {bank.available}/{bank.clips.length} clips · {(bank.gen_ms / 1000).toFixed(0)} s to generate</div>
          <div className="flex flex-wrap gap-1.5">
            {bank.clips.map((c) => <button key={c.id} data-testid={`sfx-clip-${c.id}`} disabled={!c.audio_path} className="btn-lab px-2 py-1 text-[0.6rem]" onClick={() => play(c)}>{c.id}</button>)}
          </div>
        </>
      ) : <div className="text-[var(--dim)]">no bank yet</div>}
    </Panel>
  );
};
